import type { I18nDictionaryShape } from '../../types';
import type { featuresEnGb } from '../en-GB/features';

export const featuresSrRs = {
  annotations: {
    aiFeedbackAlert: {
      desktopPrompt: 'Kliknite desnim tasterom na fotografiju da pošaljete AI povratnu informaciju!',
      improveAccuracy: 'Poboljšajte tačnost.',
      improveAccuracyFor: 'Poboljšajte tačnost za {{tradeCompanyName}}.',
      mobilePrompt: 'Dodirnite i zadržite fotografiju da pošaljete AI povratnu informaciju!',
    },
    aiFeedbackSwitch: {
      label: 'Prikaži AI povratne informacije',
    },
    aiFilters: {
      analysisFailedDescription:
        'Naš tim će to proveriti. U međuvremenu nastavite sa preostalim fotografijama u izveštaju.',
      analysisFailedTitle: 'AI nije uspeo da analizira ovu fotografiju',
      description: 'Prikažite ili sakrijte obrise materijala i uporedite ih sa originalnom fotografijom.',
      noFiltersDescription:
        'Safi AI iscrtava samo materijal koji nije cilj (kao što su nečistoće ili dodaci). Ova fotografija sadrži samo ciljni materijal (npr. UBC ili PET), pa nema obrisa ni filtera za prikaz.',
      noFiltersTitle: 'Nema filtera za prikaz jer je otkriveno 100% ciljnog materijala',
      showOutlines: 'Prikaži AI obrise',
      title: 'Podesi AI filtere',
    },
    commentBubble: {
      actions: {
        deleteFeedback: 'Obriši povratnu informaciju',
        edit: 'Izmeni',
      },
      adminMeta: 'ID:{{commentId}} AI:{{aiVersion}}',
      deleteDialog: {
        confirm: 'Obriši',
        message: 'Da li ste sigurni da želite da obrišete ovu povratnu informaciju? Ova radnja ne može da se opozove.',
        title: 'Obriši povratnu informaciju',
      },
    },
    commentFormDrawer: {
      actions: {
        cancel: 'Otkaži',
        save: 'Sačuvaj',
      },
      feedbackType: {
        incorrectDetection: 'Pogrešno detektovan objekat',
        missingDetection: 'Objekat nije detektovan',
      },
      labels: {
        note: 'Napomena',
      },
      titleCreate: 'Pošaljite AI povratnu informaciju',
      titleEdit: 'Izmenite AI povratnu informaciju',
    },
    helpAndFeedbackBar: {
      downloadFullImageAriaLabel: 'Preuzmi celu fotografiju dokaza',
    },
    segmentationOverlay: {
      ariaLabel: 'Slika sa segmentacionim slojem',
      helpText: {
        desktop: 'Pređite preko fotografije da uvećate',
        mobile: 'Uvećajte prstima',
      },
    },
    speechBubbleAvatar: {
      viewCommentAriaLabel: 'Pogledajte komentar korisnika {{fullName}}',
    },
  },
  auth: {
    companySignUpForm: {
      actions: {
        next: 'Dalje',
      },
      labels: {
        companyType: 'Tip kompanije',
        country: 'Država',
        name: 'Naziv',
      },
      notifications: {
        errorSigningUp: 'Greška pri registraciji',
      },
      validation: {
        companyTypeRequired: 'Tip kompanije je obavezan',
        countryRequired: 'Država je obavezna',
        nameRequired: 'Naziv je obavezan',
      },
    },
    otpForm: {
      actions: {
        signIn: 'Prijavi se',
      },
      labels: {
        passcode: 'Kod',
      },
      validation: {
        digitsOnly: 'Mora da sadrži samo cifre',
        passcodeLength: 'Jednokratni kod mora da ima 4 cifre',
        passcodeRequired: 'Jednokratni kod je obavezan',
      },
    },
    signInEmailForm: {
      actions: {
        next: 'Dalje',
      },
      labels: {
        email: 'E-pošta',
      },
      links: {
        signInWithPhone: 'Prijavi se brojem telefona',
      },
      validation: {
        emailRequired: 'E-pošta je obavezna',
        invalidEmail: 'Nevažeća e-pošta',
        termsRequired: 'Morate da prihvatite uslove korišćenja',
      },
    },
    signInPage: {
      subtitle:
        'Prijavite se na {{brandName}} poslovnom e-poštom ili brojem telefona. Poslaćemo vam jednokratni kod za brži pristup.',
      title: 'Kontrola kvaliteta sa poverenjem',
    },
    signInPhoneForm: {
      actions: {
        next: 'Dalje',
        signInWithEmail: 'Prijavi se e-poštom',
      },
      labels: {
        phoneNumber: 'Broj telefona',
      },
      validation: {
        phoneNumberRequired: 'Broj telefona je obavezan',
      },
    },
    termsAndPolicyAgreement: {
      fullSentence:
        'Prihvatam <termsLink>Uslove korišćenja</termsLink> i <privacyLink>Politiku privatnosti</privacyLink> pristupanjem na {{brandName}}.',
    },
    userSignUpForm: {
      actions: {
        next: 'Dalje',
      },
      labels: {
        email: 'E-pošta',
        fullName: 'Puno ime',
        phoneNumber: 'Broj telefona',
      },
      notifications: {
        errorSigningUp: 'Greška pri registraciji',
      },
      validation: {
        emailRequired: 'E-pošta je obavezna',
        fullNameRequired: 'Puno ime je obavezno',
        invalidEmail: 'Nevažeća e-pošta',
        phoneNumberRequired: 'Broj telefona je obavezan',
        termsRequired: 'Morate da prihvatite uslove korišćenja',
      },
    },
  },
  backlog: {
    breakdowns: {
      evaluatedSpecsItems: {
        ungroupedMaterials: 'Negrupisani materijali',
      },
      formatDirtScore: {
        average: 'Prosečno',
        clean: 'Čisto',
        dirty: 'Prljavo',
        notAvailable: 'N/A',
        veryClean: 'Veoma čisto',
      },
      materialAiBreakdown: {
        aiModelNotSupported: 'AI model nije podržan',
      },
      materialLabels: {
        aluminium: 'Aluminijum',
        aluminiumAerosol: 'Aluminijumski aerosol',
        aluminiumFoodCan: 'Aluminijumska konzerva za hranu',
        brown: 'Braon',
        clear: 'Providno',
        clearFullSleeveNonFoodGrade: 'Providno sa punom navlakom, nije za prehrambenu upotrebu',
        clearNonFoodGrade: 'Providno, nije za prehrambenu upotrebu',
        clearPetTray: 'Providna PET posuda',
        darkBlue: 'Tamnoplavo',
        dirtyBottles: 'Prljave flaše',
        expandedPolystyrene: 'Ekspandirani polistiren',
        film: 'Folija',
        foilAndTray: 'Folija i posuda',
        fullSleeve: 'Puna navlaka',
        green: 'Zeleno',
        hdpe: 'HDPE',
        hdpeBlack: 'Crno',
        hdpeBlue: 'Plavo',
        hdpeBrown: 'Braon',
        hdpeGreen: 'Zeleno',
        hdpeJazz: 'Jazz',
        hdpeNaturalFoodGrade: 'Prirodni za prehrambenu upotrebu',
        hdpeNaturalNonFoodGrade: 'Prirodni koji nije za prehrambenu upotrebu',
        hdpeOrange: 'Narandžasto',
        hdpePurple: 'Ljubičasto',
        hdpeRed: 'Crveno',
        hdpeSiliconTubes: 'Silikonske cevi',
        hdpeWhiteFoodGrade: 'Beli za prehrambenu upotrebu',
        hdpeWhiteNonFoodGrade: 'Beli koji nije za prehrambenu upotrebu',
        hdpeYellow: 'Žuto',
        lightBlue: 'Svetloplavo',
        metal: 'Metal',
        nonAluminium: 'Nije aluminijum',
        nonHdpe: 'Nije HDPE',
        nonMetal: 'Nije metal',
        nonPet: 'Nije PET',
        nonPlastic: 'Nije plastika',
        oilBottle: 'Flaša za ulje',
        opaque: 'Neprozirno',
        other: 'Ostalo',
        otherAluminium: 'Drugi aluminijum',
        otherColour: 'Druga boja',
        otherMetal: 'Drugi metal',
        otherPlastic: 'Druga plastika',
        pet: 'PET',
        petTrayContaminatedClear: 'Kontaminirana providna PET posuda',
        polypropylene: 'PP',
        prohibitive: 'Zabranjeno',
        total: 'Ukupno',
        ubc: 'UBC',
        waste: 'Otpad',
        white: 'Belo',
      },
      representativeEvidenceFilter: {
        label: 'Prikaži slike od:',
        options: {
          bestBales: 'Najbolje bale',
          typicalBales: 'Tipične bale',
          worstBales: 'Najslabije bale',
        },
      },
      representativeEvidenceSection: {
        distributionTitle: 'Raspodela za {{targetName}}',
      },
      specificationAlerts: {
        groupDescription:
          '<boldAction>{{actionText}}</boldAction> - Grupa <boldName>{{name}}</boldName> je <boldComparison>{{comparison}}</boldComparison>',
        groupDescriptionWithoutComparison:
          '<boldAction>{{actionText}}</boldAction> - Grupa <boldName>{{name}}</boldName> je',
        groupIs: 'grupa je',
        the: '- Grupa',
        title: 'Upozorenja',
      },
      totalWeightWarning: {
        message:
          'Upozorenje! Ukupna težina ({{formattedTotalWeight}}kg) nije zbir težina iz razrade ({{formattedSum}}kg).',
      },
    },
    cameraGuidance: {
      useCameraStream: {
        notifications: {
          cannotUseCamera: 'Kamera ne može da se koristi',
          refreshAndAllowCamera:
            'Osvežite ovu stranicu i pokušajte ponovo. Dozvolite pristup kameri kada se to zatraži.',
        },
      },
    },
    common: {
      noData: 'Nema podataka',
    },
    dealDetails: {
      fields: {
        destination: 'Odredište',
        endProcessor: 'Krajnji prerađivač',
        inspections: 'Inspekcije',
        loadingSite: 'Lokacija utovara',
        loads: 'Ture',
        material: 'Materijal',
        producer: 'Proizvođač',
        risk: 'Rizik',
      },
      header: {
        deal: 'Dogovor {{dealId}}',
      },
    },
    dealRevenueDetails: {
      columns: {
        description: 'Opis',
        id: 'ID',
        invoiceStatus: 'Status fakture',
        invoiceXeroAmountPaid: 'Plaćeni iznos fakture u Xero',
        invoiceXeroFullyPaidOn: 'U potpunosti plaćeno u Xero dana',
        invoiceXeroId: 'ID fakture u Xero',
        lineTotal: 'Ukupno po stavci',
        quantity: 'Količina',
        unitAmount: 'Iznos po jedinici',
      },
    },
    documentGeneration: {
      notifications: {
        failedToSaveFile: 'Čuvanje fajla nije uspelo',
        failedToUploadFile: 'Otpremanje fajla nije uspelo',
      },
    },
    evidence: {
      bulkDownload: {
        empty: 'Nema dokaza za preuzimanje',
        error: 'Preuzimanje dokaza nije uspelo',
        inProgress: 'Priprema preuzimanja…',
        partialSuccess: 'Preuzeto {{downloaded}} od {{total}} fotografija',
        success: 'Preuzeto {{count}} fotografija',
      },
      common: {
        editSample: 'Izmeni uzorak',
        manageInspection: 'Upravljaj inspekcijom',
      },
      evidencePhotosViewer: {
        bulkDeleteError: 'Brisanje fotografija nije uspelo',
        bulkDeleteSuccess: 'Uspešno je obrisano {{count}} fotografija',
        noEvidenceFound: 'Nema pronađenih fotografija',
      },
      loadingPhotoEvidenceGallery: {
        noOtherImagesAvailable: 'Nema drugih dostupnih slika.',
      },
      manualBreakdownFormFields: {
        kilogramUnit: 'Kg',
      },
      sessionSummary: {
        anonymous: 'Anonimno',
        withOrigin: '{{origin}} - Otpremio/la {{uploadedBy}}',
        withoutOrigin: 'Otpremio/la {{uploadedBy}}',
      },
      uploadMenu: {
        downloadAllEvidence: 'Preuzmi sve dokaze',
        downloadingAllEvidence: 'Preuzimanje…',
        editEvidence: 'Izmeni dokaze',
        menuAriaLabel: 'Više radnji',
        offlineModeComingSoon: 'Oflajn režim uskoro stiže',
        openCamera: 'Otvori kameru',
        uploadEvidence: 'Otpremi dokaze',
      },
      uploadOptionsButton: {
        actions: {
          upload: 'Otpremi',
        },
        options: {
          openCamera: 'Otvori kameru',
          uploadPhoto: 'Otpremi fotografiju',
        },
      },
    },
    evidenceGrid: {
      columns: {
        evidence: 'Dokaz',
      },
    },
    externalLoad: {
      common: {
        supplier: 'Dobavljač',
      },
    },
    findTicketsTable: {
      columns: {
        category: 'Kategorija',
        status: 'Status',
        summary: 'Sažetak',
        team: 'Tim',
      },
    },
    firmQuoteBulkForm: {
      columns: {
        bunkerAdjustmentFactorCost: 'Trošak BAF faktora',
        bunkerAdjustmentFactorCurrency: 'Valuta BAF faktora',
        carrier: 'Prevoznik',
        containerType: 'Tip kontejnera',
        demandIncoterm: 'Demand Incoterm',
        destinationCountry: 'Država odredišta',
        destinationLat: 'Geografska širina odredišta',
        destinationLon: 'Geografska dužina odredišta',
        destinationName: 'Naziv odredišta',
        directLandCost: 'Direktni kopneni trošak',
        directLandCurrency: 'Valuta direktnog kopnenog troška',
        e2eCost: 'E2E trošak',
        e2eCurrency: 'E2E valuta',
        estimatedDeparture: 'Proc. polazak',
        expiresOn: 'Ističe',
        forwarder: 'Špediter',
        notes: 'Napomene',
        oceanFreightCost: 'Trošak okeanskog prevoza',
        oceanFreightCurrency: 'Valuta okeanskog prevoza',
        onCarriageCost: 'Trošak naknadnog prevoza',
        onCarriageCurrency: 'Valuta naknadnog prevoza',
        originCountry: 'Država porekla',
        originLat: 'Geografska širina porekla',
        originLon: 'Geografska dužina porekla',
        originName: 'Naziv porekla',
        peakSeasonSurchargeCost: 'Trošak sezonske doplate',
        peakSeasonSurchargeCurrency: 'Valuta sezonske doplate',
        portOfDischarge: 'Luka istovara',
        portOfLoading: 'Luka utovara',
        preCarriageCost: 'Trošak prethodnog prevoza',
        preCarriageCurrency: 'Valuta prethodnog prevoza',
        quotedOn: 'Ponuda data',
        supplyIncoterm: 'Supply Incoterm',
        typeOfGoods: 'Vrsta robe',
      },
    },
    grade: {
      compositionReport: {
        annotatedBaleAlt: 'Anotirana bala',
      },
      imageQualityPrompt: {
        label: 'Fotografiši ponovo',
      },
    },
    groundControl: {
      common: {
        descriptions: {
          calculateDealProfitability: 'Izračunaj profitabilnost dogovora',
          createPurchaseOrderAndSendToSeller: 'Kreiraj nalog za kupovinu i pošalji ga prodavcu na potpis',
          createSalesOrderAndSendToBuyer: 'Kreiraj prodajni nalog i pošalji ga kupcu na potpis',
          viewEditAndMatch: 'Pregledaj, izmeni i upari',
        },
        labels: {
          createPurchaseOrder: 'Kreiraj nalog za kupovinu',
          createSalesOrder: 'Kreiraj prodajni nalog',
          dealProfitabilityCalculator: 'Kalkulator profitabilnosti dogovora',
          viewPurchaseOrders: 'Prikaži naloge za kupovinu',
          viewSalesOrders: 'Prikaži prodajne naloge',
        },
      },
      tabs: {
        advanced: {
          label: 'Napredno',
          links: {
            addExternalEvidence: {
              description: 'Dodaj dokaze iz eksternih izvora',
              label: 'Dodaj eksterne dokaze',
            },
            manageMaterialProducts: {
              description: 'Pregledaj i izmeni proizvode materijala',
              label: 'Upravljaj proizvodima materijala',
            },
            pernType: {
              description: 'Ažuriraj PERN tipove',
              label: 'PERN tip',
            },
          },
        },
        contracts: {
          label: 'Ugovori',
        },
        deals: {
          label: 'Dogovori',
          links: {
            goToDealHub: {
              description: 'Pregledaj i obradi zadatke vezane za dogovore',
              label: 'Idi na DealHub',
            },
            viewAllDeals: {
              description: 'Pregledaj i izmeni sve dogovore i ture',
              label: 'Prikaži sve dogovore',
            },
          },
        },
        finance: {
          label: 'Finansije',
          links: {
            createShippingRate: {
              description: 'Kreiraj tarifu prevoza i sve povezane detalje',
              label: 'Kreiraj tarifu prevoza',
            },
            manageBanks: {
              description: 'Pregledaj i izmeni bankovne podatke',
              label: 'Upravljaj bankama',
            },
            manageCosts: {
              description: 'Kreiraj, izmeni i pregledaj troškove',
              label: 'Upravljaj troškovima',
            },
            manageExternalCompanies: {
              description: 'Kreiraj, izmeni i pregledaj podatke eksternih kompanija',
              label: 'Upravljaj eksternim kompanijama',
            },
            manageFinancing: {
              description: 'Pošalji zahteve za plaćanje Finmid-u',
              label: 'Upravljaj finansiranjem',
            },
            manageRevenue: {
              description: 'Kreiraj, izmeni i pregledaj prihode',
              label: 'Upravljaj prihodima',
            },
            marketPernPrices: {
              description: 'Pregledaj istorijske PERN cene iz T2E',
              label: 'Tržišne PERN cene',
            },
          },
        },
        support: {
          label: 'Podrška',
          links: {
            getSupport: {
              description: 'Kreiraj Jira tiket za podršku',
              label: 'Zatraži podršku',
            },
            helpAndLearn: {
              description: 'Vodiči za pomoć',
              label: 'Pomoć i učenje',
            },
          },
        },
        trade: {
          label: 'Trgovina',
          links: {
            generateDocuments: {
              description: 'Generiši dokumenta za naloge za kupovinu, prodajne naloge, pošiljke i slično.',
              label: 'Generiši dokumenta',
            },
            transportMetabase: {
              description: 'Link ka Transport Metabase-u',
              label: 'Transport Metabase',
            },
          },
        },
        usersAndCompanies: {
          label: 'Korisnici i kompanije',
          links: {
            copySessions: {
              description: 'Kopiraj sesije u ML Accuracy Testing putem CSV uvoza',
              label: 'Kopiraj sesije',
            },
            createUserAndCompany: {
              description: 'Kreiraj korisnika i novu kompaniju ili pozovi u postojeću kompaniju',
              label: 'Kreiraj korisnika i kompaniju',
            },
            hijackUser: {
              description: 'Brzo preuzmi korisnički nalog',
              label: 'Preuzmi korisnika',
            },
            staffOnboarding: {
              description: 'Kreiraj novog admin korisnika i dodaj ga u Safi',
              label: 'Uvođenje zaposlenih',
            },
            viewTradeCompanies: {
              description: 'Pregledaj i izmeni trgovačke kompanije za ponudu i potražnju',
              label: 'Prikaži sve trgovačke kompanije',
            },
          },
        },
      },
    },
    guidePriceChip: {
      label: 'Orijentaciona cena',
      unavailable: 'Orijentaciona cena nije dostupna',
    },
    helpGuides: {
      aiBale: {
        partOne: {
          boldText: 'Stanite najviše 1 metar dalje',
          imageAlt: 'Stanite najviše 1 metar dalje',
          trailingText: 'ispred gomile.',
        },
      },
      aiLoose: {
        partFour: {
          and: 'i',
          clearPile: 'Sklonite gomilu',
          imageAlt: 'Sklonjena gomila',
          repeatSteps: 'ponovite korake 1-3',
          untilText: 'dok ne napravite po jednu fotografiju za svaku gomilu u celom uzorku.',
        },
        partOne: {
          imageAlt: 'Napravite gomilu',
          makePile: 'Napravite gomilu',
          middleText: 'od svog uzorka i ravnomerno je rasporedite po podu.',
          takePhoto: 'Napravite 1 fotografiju po gomili.',
        },
        partThree: {
          boldIntro: 'Proverite fotografiju pre nego što sklonite gomilu.',
          imageAlt: 'Pregledaj i obriši',
          trailingText: 'Ako nije dobra, možete da je obrišete i napravite novu.',
          warningBold: 'Samo 1 fotografija po gomili',
          warningPrefix: 'Otpremite',
          warningSuffix: 'da ne biste iskrivili AI rezultate.',
        },
      },
      common: {
        completeAlt: 'Završeno',
      },
      data: {
        guides: {
          aiBale: 'AI bala',
          aiBriquette: 'AI briket',
          aiLoose: 'AI rasuto',
          emailSupport: 'Pišite nam na support@safi.co',
        },
        sections: {
          contactSupport: 'Kontaktirajte podršku',
          qualityControlGuides: 'Vodiči za kontrolu kvaliteta',
        },
        subcategories: {
          emailSupportPrompt: 'Trebate podršku ili želite zatražiti novu funkcionalnost?',
          howToTakeGreatAiPhotos: 'Kako napraviti dobre AI fotografije',
        },
      },
    },
    manageDataButton: {
      buttonLabel: 'Upravljaj podacima',
      options: {
        aiBalePhotos: 'AI fotografije bala',
        loadingEvidence: 'Dokazi o utovaru',
        marketingMedia: 'Marketing mediji',
        samples: 'Uzorci',
        siteEvidence: 'Dokazi sa lokacije',
        specifications: 'Specifikacije',
      },
    },
    materialCategoryActions: {
      notifications: {
        cannotSaveMaterial: 'Materijal ne može da se sačuva',
        errorCreatingMaterial: 'Greška pri kreiranju materijala',
        errorDeletingMaterial: 'Greška pri brisanju materijala',
        errorSavingMaterial: 'Greška pri čuvanju materijala',
        savedSuccessfully: 'Uspešno sačuvano',
        selectedTargetGroupHasNoOperands: 'Izabrana ciljna grupa nema operande. Izaberite drugu ciljnu grupu.',
        skippedSpecifications: '{{count}} specifikacija nije sačuvano jer im nisu dodeljeni operandi.',
      },
    },
    materialComponents: {
      supportedComponentList: {
        buttonText: 'Dodaj komponentu materijala',
        title: 'Komponente materijala',
      },
    },
    materialDirtLevel: {
      options: {
        clean: 'Čisto',
        dirty: 'Prljavo',
        notAvailable: 'N/A',
        veryClean: 'Veoma čisto',
      },
    },
    materialProducts: {
      validation: {
        atLeastOneBuyerMaterialComponentIsRequired: 'Potrebna je najmanje jedna komponenta materijala kupca.',
        atLeastOneSellerMaterialComponentIsRequired: 'Potrebna je najmanje jedna komponenta materijala prodavca.',
        buyerAndSellerMaterialComponentCountsMustMatch:
          'Broj vrednosti komponenti materijala kupca i prodavca mora biti isti.',
      },
    },
    materialProductsList: {
      columns: {
        buyerMaterialComponents: 'Komponente materijala kupca',
        capsAndLabels: 'Čepovi i etikete',
        country: 'Država',
        foodGrade: 'Za prehrambenu upotrebu',
        id: 'ID',
        materialGrade: 'Razred materijala',
        materialOrigin: 'Poreklo materijala',
        materialSpec: 'Specifikacija materijala',
        process: 'Proces',
        sellerMaterialComponents: 'Komponente materijala prodavca',
      },
      values: {
        no: 'Ne',
        notSpecified: 'N/A',
        yes: 'Da',
      },
    },
    notificationParty: {
      label: 'Strana za obaveštavanje',
      options: {
        allViaAgent: 'Sva komunikacija preko agenta',
        directWithBuyer: 'Komuniciraj direktno sa kupcem',
      },
    },
    orders: {
      validation: {
        fixedPriceAndDateMustBeProvidedTogether: 'Fiksna cena i fiksni datum moraju biti navedeni zajedno',
      },
    },
    paymentMethod: {
      label: 'Način plaćanja',
      options: {
        documentAgainstPayment: 'Dokumenta uz plaćanje',
        telegraphicTransfer: 'Telegrafski prenos',
      },
    },
    percentageLightBlue: {
      label: 'Procenat svetloplavog',
      options: {
        notAvailable: 'N/A',
      },
    },
    siteMaterials: {
      notifications: {
        errorCreatingMaterial: 'Greška pri kreiranju materijala',
        materialCreated: 'Materijal je kreiran',
        productLinkedSuccessfully: 'Proizvod je uspešno povezan',
      },
    },
    timeFilter: {
      options: {
        last30Days: 'Poslednjih 30 dana',
        last7Days: 'Poslednjih 7 dana',
        last90Days: 'Poslednjih 90 dana',
        next14Days: 'Narednih 14 dana',
        next2Days: 'Naredna 2 dana',
        next7Days: 'Narednih 7 dana',
      },
    },
    variablePricingFields: {
      indexOptions: {
        lmeAluminium: 'LME aluminijum',
        lmeAluminiumMidwestPremium: 'LME aluminijum + Midwest Premium',
      },
    },
  },
  breakdowns: {
    adminAiVersionTitle: {
      value: 'AI:{{version}}',
    },
    aiSampleBreakdown: {
      analysisNotAvailable: 'Analiza nije dostupna',
    },
    aluMaterialSampleBreakdown: {
      labels: {
        aluminium: 'Aluminijum',
        foilAndTray: 'Folija i posuda',
        foodCan: 'Konzerva za hranu',
        nonAluminium: 'Nije aluminijum',
        nonMetal: 'Nije metal',
        otherAluminium: 'Drugi aluminijum',
        otherMetal: 'Drugi metal',
        total: 'Ukupno',
        ubc: 'UBC',
      },
    },
    analysisInProgressAlert: {
      partialDescription:
        'Rezultati ispod su zasnovani na {{acceptedCount}} od {{submittedCount}} fotografija. Još {{pendingCount}} se analizira.',
      partialTitle: 'Analizira se još fotografija',
      pendingDescription:
        '{{pendingCount}} od {{submittedCount}} fotografija se još analizira. Rezultati bi trebalo da se pojave za nekoliko minuta.',
      pendingTitle: 'Analiza je u toku',
      photoPendingDescription:
        'Vaša fotografija se još analizira u pozadini. Rezultati i konture bi trebalo da se pojave za nekoliko minuta.',
      rejectedDescription: '{{rejectedCount}} fotografija je odbijeno. Proverite razlog na svakoj fotografiji.',
    },
    cleanliness: {
      label: 'Čistoća',
      tooltip:
        'Čistoća pokazuje koliko je materijal čist. Skala ide od veoma čisto, čisto, prosečno do prljavo. Prljavština uključuje masnoću, fleke i prašinu na površini materijala.',
    },
    evaluatedSpecsBreakdown: {
      customizeTableFor: 'Prilagodi tabelu za',
      targetChip: 'Cilj',
      thisMaterial: 'ovaj materijal',
      title: 'AI analiza',
    },
    noAnalysisAlert: {
      description:
        'AI nije uspeo da analizira fotografije. Možete proveriti razlog za svaku fotografiju, obrisati odbijene i pokušati ponovo.',
      guidance: 'Pogledajte <guideLink>{{guideLabel}} vodič</guideLink> za savete kako da napravite bolje fotografije.',
      noSubmittedPhotosDescription:
        'Još nisu poslate fotografije za analizu. Dodajte fotografije da biste pokrenuli analizu.',
      title: 'Nema dostupnih rezultata',
    },
    petFlake: {
      fields: {
        bulkDensity: 'Nasipna gustina',
        clearFlakes: 'Providne pahuljice',
        colouredFlakes: 'Obojene pahuljice',
        darkBlueFlakes: 'Tamnoplave pahuljice',
        ferrousMetal: 'Gvožđe',
        glue: 'Lepak',
        greenFlakes: 'Zelene pahuljice',
        intrinsicViscosity: 'Intrinzična viskoznost',
        isophthalicAcid: 'Izofthalna kiselina',
        lightBlueFlakes: 'Svetloplave pahuljice',
        meltFlowRate: 'Indeks protoka topljenja (MF) (260°C/2,16Kg)',
        meltingPoint: 'Tačka topljenja',
        nonFerrousMetal: 'Obojeni metal',
        otherImpurities: 'Druge nečistoće',
        paper: 'Papir',
        particlesSizeBetween10And12mm: 'Čestice veličine između 10 i 12 mm',
        particlesSizeBetween5And10mm: 'Čestice veličine između 5 i 10 mm',
        particlesSizeLt5mm: 'Čestice veličine < 5 mm',
        petContent: 'Sadržaj polietilen-tereftalata (PET)',
        ph: 'pH',
        polyolefinContamination: 'Kontaminacija poliolefinom',
        pvcContamination: 'Kontaminacija PVC-om',
        residualContamination: 'Preostala kontaminacija',
        residualMoisture: 'Preostala vlaga',
        sampledBy: 'Uzorak uzeo/la',
        woodenMaterials: 'Drveni materijali',
        yellowFlakes: 'Žute pahuljice',
      },
    },
    petMaterialSampleBreakdown: {
      labels: {
        clear: 'Providno',
        clearFullSleeveNonFoodGrade: 'Providno sa punom navlakom, nije za prehrambenu upotrebu',
        clearNonFoodGrade: 'Providno, nije za prehrambenu upotrebu',
        clearPetTray: 'Providna PET posuda',
        darkBlue: 'Tamnoplavo',
        film: 'Folija',
        green: 'Zeleno',
        largeClear5L: 'Veliko providno (≥5L)',
        lightBlue: 'Svetloplavo',
        liquid: 'Tečnost',
        nonPetBottle: 'Flaša koja nije PET',
        nonPlastic: 'Nije plastika',
        other: 'Ostalo',
        otherPlastic: 'Druga plastika',
        petBottle: 'PET flaša',
        prohibitive: 'Zabranjeno',
        total: 'Ukupno',
      },
    },
    taintTaborMaterialSampleBreakdown: {
      labels: {
        aluIron: 'Alu./gvožđe',
        aluNonMetal: 'Alu./nemetal',
        aluWithAttachments: 'Alu. sa dodacima',
        aluminium: 'Aluminijum',
        cast: 'Liveno',
        castIron: 'Liveno/gvožđe',
        ferrousMetal: 'Gvožđe',
        finesLt20mm: 'Sitni delovi <20 mm',
        nonAluminium: 'Nije aluminijum',
        nonMetal: 'Nije metal',
        otherMetal: 'Drugi metal',
        prohibitive: 'Zabranjeno',
        radiatorCondenser: 'Hladnjak/Kondenzator',
        stainlessSteel: 'Nerđajući čelik',
        taintTabor: 'Taint Tabor',
        total: 'Ukupno',
        ubcFoil: 'UBC/Folija',
      },
    },
    zorbaMaterialSampleBreakdown: {
      labels: {
        aluminiumUbc: 'Aluminijum - UBC',
        compositeMaterials: 'Kompozitni materijali',
        copperBrassRedsYellows: "Bakar i mesing - 'crveni i žuti'",
        finesLt20mm: 'Sitni delovi <20 mm',
        nonFerrousMetalWhites: "Obojeni metal - 'beli'",
        nonTarget: 'Materijal van cilja',
        otherMetalFerrous: 'Drugi metal (gvožđe)',
        otherNonMetal: 'Drugi nemetal',
        postConsumerAluminiumPackaging: 'Aluminijumska ambalaža nakon potrošnje',
        prohibitive: 'Zabranjeno',
        targetMaterial: 'Ciljni materijal',
        total: 'Ukupno',
      },
    },
  },
  cameraGuidance: {
    camera: {
      notifications: {
        autoCaptureOff: 'Automatsko okidanje isključeno',
        autoCaptureOn: 'Automatsko okidanje uključeno',
      },
      rejectedPhotos: {
        message: 'Dodirnite fotografiju ispod da vidite razlog greške, zatim obrišite fotografiju i pokušajte ponovo',
        photo: 'fotografiju',
        photos: 'fotografije',
        title: '{{prefix}} da proverite {{count}} {{countLabel}}!',
        titlePrefixObfuscated: 'Neuspešno',
        titlePrefixWithAi: 'AI nije uspeo',
      },
    },
    cameraFooter: {
      actions: {
        capture: 'Snimi',
        complete: 'Završi',
        guide: 'Vodič',
      },
      tooltips: {
        takePhotoToComplete: 'Napravite fotografiju da biste završili',
      },
    },
    cameraHeader: {
      autoCapture: 'Automatsko okidanje',
      confirmationDialog: {
        actions: {
          complete: 'Završi',
          exitAnyway: 'Izađi ipak',
        },
        messageWithAiLabel:
          'Vaše fotografije će biti sačuvane, ali AI rezultate nećete odmah videti. Dodirnite Završi da odmah prikažete AI izveštaj.',
        messageWithoutAiLabel:
          'Vaše fotografije će biti sačuvane, ali rezultate nećete odmah videti. Dodirnite Završi da odmah prikažete izveštaj.',
        title: 'Napustiti kameru?',
      },
      flashAriaLabel: 'Uključi ili isključi blic',
      settings: {
        description: 'Koristite oflajn režim ako vam je veza slaba da ne biste izgubili fotografije',
        title: 'Podešavanja',
      },
    },
    countdownOverlay: {
      snap: 'Škljoc!',
    },
    previewImageReview: {
      actions: {
        deleteAndRetake: 'Obriši i fotografiši ponovo',
      },
      evidencePhotoAlt: 'Fotografija dokaza',
      notifications: {
        deleteFailed: 'Nije moguće obrisati ovu fotografiju. Pokušajte ponovo.',
      },
    },
  },
  charts: {
    targetBaleWeightsHistogram: {
      axes: {
        x: 'Predviđeni procenat ciljnog materijala %',
        y: 'Broj bala',
      },
      labels: {
        average: 'Prosek: {{targetGrade}}%',
        bales: 'Bale',
      },
      tooltip: {
        label: 'Detektovano %: {{label}}',
        value: '{{value}} bala',
      },
    },
  },
  dataCards: {
    aluminiumComposition: {
      reportTitle: 'Izveštaj',
      targetDistributionTitle: 'Raspodela ciljnog materijala',
    },
    common: {
      loads: 'Ture',
      metricTonnes: 'Metričke tone',
    },
    hdpeComposition: {
      aiBaleAnalysisTitle: 'AI analiza bala',
      reportTitle: 'Izveštaj',
    },
    loadingDetails: {
      actions: {
        showMedia: 'Prikaži medije',
      },
      fields: {
        loadingNotes: 'Napomene o utovaru',
        loadingTechnique: 'Tehnika utovara',
        packedOn: 'Upakovano',
        targetPayload: 'Ciljni teret',
      },
      title: 'Detalji utovara',
      values: {
        targetPayloadMt: '{{targetPayload}} (MT)',
      },
    },
    materialNotAnalysed: {
      analysisTitle: 'Analiza',
      noResultsDescription:
        'Još nismo razvili model za ovaj specifičan tip materijala. Ali sa više podataka, bićemo u mogućnosti da pružimo analizu u budućnosti.',
      noResultsTitle: 'Nema dostupnih rezultata',
      reportTitle: 'Izveštaj',
    },
    materialSpecifications: {
      actions: {
        showMedia: 'Prikaži medije',
      },
      analysis: {
        completed: 'Završeno',
        notCompleted: 'Nije završeno',
      },
      fields: {
        baleDimensions: 'Dimenzije bale',
        capsAndLabels: 'Čepovi i etikete',
        density: 'Gustina (kg/m³)',
        ferrousCheck: 'Provera gvožđa',
        foodGrade: 'Za prehrambenu upotrebu',
        materialAnalysisNotes: 'Napomene o analizi materijala',
        materialForm: 'Oblik materijala',
        materialProcess: 'Proces materijala',
        origin: 'Poreklo',
        radiationCheck: 'Provera zračenja',
        strapping: 'Vezivanje',
        visualCheck: 'Vizuelna provera',
      },
      title: 'Specifikacije materijala',
      units: {
        meters: 'm',
      },
      values: {
        no: 'Ne',
        yes: 'Da',
      },
    },
    petComposition: {
      aiBaleAnalysisTitle: 'AI analiza bala',
      reportTitle: 'Izveštaj',
    },
    site: {
      actions: {
        showMedia: 'Prikaži medije',
      },
      fields: {
        machinesUsed: 'Korišćene mašine',
        materialOrigin: 'Poreklo materijala',
        monthlyVolume: 'Mesečni obim',
        siteGround: 'Podloga lokacije',
        siteNotes: 'Napomene o lokaciji',
        storageAndCover: 'Skladištenje i pokrivanje',
      },
      monthlyVolumeValue: '{{productionVolume}} (MT)',
      title: 'Lokacija',
    },
    tradeCompanySessionLoose: {
      aiLooseAnalysisTitle: 'AI analiza rasutog materijala',
      reportTitle: 'Izveštaj',
      sampleTotalWeightTitle: 'Ukupna težina uzorka',
    },
    transactionHistory: {
      alerts: {
        newSupplier: 'Novi dobavljač! Ranije niste kupovali od njih.',
        purchasedBefore: 'Ranije ste već kupovali od ovog dobavljača!',
      },
      title: 'Istorija transakcija',
      total: {
        description: 'Transakcije između ovog dobavljača i drugih kupaca u prethodnih 6 meseci.',
      },
    },
  },
  error: {
    actions: {
      contactSupport: 'Kontaktirajte podršku',
      goToHomepage: 'Idi na početnu',
      tryAgain: 'Pokušajte ponovo',
    },
    forbidden: {
      description: 'Nemate dozvolu za pregled ove stranice.',
      title: 'Pristup odbijen',
    },
    generic: {
      description: 'Došlo je do neočekivane greške. Pokušajte ponovo.',
      title: 'Nešto je pošlo naopako',
    },
    network: {
      description: 'Proverite internet vezu i pokušajte ponovo.',
      title: 'Izgleda da ste offline',
    },
    notFound: {
      description: 'Nismo mogli da pronađemo stranicu koju tražite.',
      title: 'Stranica nije pronađena',
    },
    server: {
      description: 'Imamo problema sa povezivanjem na naše servere. Pokušajte ponovo za trenutak.',
      title: 'Nešto je pošlo naopako',
    },
    support: {
      body: 'Naišao sam na grešku i potrebna mi je pomoć.\n\nStranica: {{url}}\nDetalji: {{detail}}\n\nŠta sam radio:',
      subject: 'Safi Quality — Potrebna mi je pomoć sa greškom',
    },
  },
  evidence: {
    actionBar: {
      deletePrompt: 'Da li ste sigurni da želite da obrišete izabrane dokaze?',
      deleteTitle: 'Obriši {{evidence}}',
      selectedCount: '{{selected}} izabrano',
    },
    chips: {
      ai: 'AI',
      selfServe: 'Self-Serve',
    },
    defaultBreakdownFormFields: {
      actions: {
        addBreakdownItem: 'Dodaj stavku razrade',
      },
      labels: {
        name: 'Naziv',
        unit: 'Jedinica',
        value: 'Vrednost',
      },
      title: 'Razrada',
    },
    editForm: {
      actions: {
        delete: 'Obriši',
        edit: 'Izmeni',
      },
      labels: {
        newEvidenceType: 'Novi tip {{evidence}}',
        newLabel: 'Novo',
        typeLabel: 'Tip',
      },
      notifications: {
        deleteError: 'Greška pri brisanju dokaza',
        deleteSuccess: 'Dokaz je obrisan',
        updateError: 'Greška pri ažuriranju dokaza',
        updateSuccess: 'Dokaz je ažuriran',
      },
      validation: {
        typeOrSiteMaterialRequired: 'Mora biti naveden ili tip dokaza ili materijal lokacije',
      },
      warnings: {
        deleteMessage:
          'Upravo ćete obrisati ovaj dokaz sa materijala ove lokacije. To će ga ukloniti iz povezanog izveštaja o kvalitetu. Potvrdite da nastavite.',
        editMessage:
          'Upravo ćete izmeniti ovaj dokaz. To će ga ukloniti iz povezanog izveštaja o kvalitetu. Potvrdite da nastavite.',
        title: 'Upozorenje!',
      },
    },
    inspectionSessionDetails: {
      labels: {
        dealId: 'ID dogovora:',
        inspectionSlug: 'Slug inspekcije:',
      },
      tooltips: {
        reportAvailableOnCompletion: 'Kada inspekcija bude završena, ovde ćete pronaći link ka izveštaju.',
        searchInDrive: 'Pretraži inspekciju u Google Drive-u',
        viewDealDetails: 'Prikaži detalje dogovora',
        viewExternalReport: 'Prikaži eksterni izveštaj',
      },
    },
    offlineGallery: {
      notifications: {
        cachedClearError: 'Brisanje keširanih fotografija nije uspelo',
        cachedClearSuccess: 'Keširane fotografije su obrisane',
        cachedSyncError: 'Sinhronizacija keširanih fotografija nije uspela',
        cachedSyncSuccess: 'Fotografije su otpremljene. Analiza može da potraje nekoliko minuta.',
        clearError: 'Brisanje oflajn dokaza nije uspelo',
        clearSuccess: 'Oflajn dokazi su uspešno obrisani',
        missingParentViewCache:
          'Jedna ili više keširanih fotografija pripada izveštaju koji više ne postoji. Prikažite keširane fotografije da biste ih preuzeli i ponovo otpremili ili obrisali.',
      },
      title: 'Oflajn {{evidenceLower}}',
      tooltips: {
        cannotSyncWithoutInternet: 'Sinhronizacija nije moguća bez internet veze',
        clear: 'Obriši oflajn dokaze',
        sync: 'Sinhronizuj oflajn dokaze',
        syncInProgress: 'Sinhronizacija je u toku...',
      },
    },
    sampleBreakdown: {
      labels: {
        aiBreakdown: 'AI razrada',
        manualBreakdown: 'Ručna razrada',
        noNotes: 'Nema napomena',
        sample: 'Uzorak',
        sampleEvidence: 'Dokazi o uzorku',
      },
      totalWeight: '{{totalWeight}}kg',
    },
    sampleFormFields: {
      breakdowns: {
        alu: {
          groups: {
            aluminium: 'Aluminijum',
            nonAluminium: 'Nije aluminijum',
          },
          items: {
            foilAndTray: 'Folija i posuda',
            foodCan: 'Konzerva za hranu',
            nonMetal: 'Nemetal',
            otherAluminium: 'Drugi aluminijum',
            otherAluminiumAerosols: 'Drugi aluminijum (aerosoli)',
            otherMetal: 'Drugi metal',
            rigidPlastics: 'Tvrda plastika',
            ubc: 'UBC',
            vapes: 'Elektronske cigarete',
          },
        },
        pet: {
          groups: {
            otherPetAndPlastics: 'Drugi PET i plastika',
            petBottles: 'PET flaše',
          },
          items: {
            clear: 'Providno',
            clearFullSleeve: 'Providno (puna navlaka)',
            clearLarge: 'Providno (veliko)',
            clearPetTray: 'Providna PET posuda',
            darkBlue: 'Tamnoplavo',
            film: 'Folija',
            green: 'Zeleno',
            lightBlue: 'Svetloplavo',
            liquid: 'Tečnost',
            nonFoodGradeBottle: 'PET flaša koja nije za prehrambenu upotrebu',
            nonPlastic: 'Nije plastika',
            other: 'Ostalo',
            otherPlastic: 'Druga plastika',
            prohibitive: 'Zabranjeno',
          },
        },
        taintTabor: {
          groups: {
            aluWithAttachments: 'Alu. sa dodacima',
            aluminium: 'Aluminijum',
            nonAluminium: 'Nije aluminijum',
          },
          items: {
            aluIron: 'Alu./gvožđe',
            aluNonMetal: 'Alu./nemetal',
            cast: 'Liveno',
            castIron: 'Liveno/gvožđe',
            ferrousMetal: 'Gvožđe',
            finesLt20mm: 'Sitni delovi <20mm',
            nonMetal: 'Nemetal',
            otherMetal: 'Drugi metal',
            prohibitive: 'Zabranjeno',
            radiatorCondenser: 'Hladnjak/Kondenzator',
            stainlessSteel: 'Nerđajući čelik',
            taintTabor: 'Taint Tabor',
            ubcFoil: 'UBC/Folija',
          },
        },
        zorba: {
          groups: {
            nonTarget: 'Materijal van cilja',
            targetMaterial: 'Ciljni materijal',
          },
          items: {
            aluminiumFoilUbc: 'Aluminijum - folija / UBC',
            compositeMaterials: 'Kompozitni materijali',
            copperAndBrass: "Bakar i mesing 'crveni i žuti'",
            finesLt20mm: 'Sitni delovi <20mm',
            nonFerrousMetal: "Obojeni metal 'beli'",
            otherMetalFerrous: 'Drugi metal (gvožđe)',
            otherNonMetal: 'Drugi nemetal',
            postConsumerAluminiumPackaging: 'Aluminijumska ambalaža nakon potrošnje',
            prohibitive: 'Zabranjeno',
          },
        },
      },
      labels: {
        notes: 'Napomene',
        totalWeight: 'Ukupna težina',
        weightUnit: 'Kg',
      },
    },
    sampleMaterialEditForm: {
      actions: {
        edit: 'Izmeni',
      },
      warning: {
        editMessage:
          'Upravo ćete izmeniti ovaj dokaz. To će ga ukloniti iz povezanog izveštaja o kvalitetu. Potvrdite da nastavite.',
        title: 'Upozorenje!',
      },
    },
    sampleSessionDetails: {
      manualSampleAnalysisTitle: 'Analiza ručnog uzorka',
      noData: 'Nema podataka',
      sampleImagesTitle: 'Slike uzorka',
    },
    sessionStats: {
      excludedSubtitle: 'Slike nisu ispunile naša pravila zahteva',
      excludedTitle: 'Isključeno',
      includedSubtitle: 'Slike su ispunile naša pravila zahteva',
      includedTitle: 'Uključeno',
    },
    unloadingPhotoUploader: {
      label: 'Otpremi fotografije',
      notifications: {
        uploadFailed: 'Otpremanje nije uspelo',
        uploadSuccess: 'Otpremanje uspešno',
      },
    },
  },
  helpGuides: {
    aiBale: {
      partTwo: {
        captions: {
          clearWhichBaleToFocusOn: 'Jasno je na koju balu treba da se fokusirate',
          goodQualityPhoto: 'Fotografija dobrog kvaliteta',
          goodQualityPhotoAndDistanceFromBale: 'Dobar kvalitet fotografije i dobra udaljenost od bale',
          greatDistanceFromBale: 'Odlična udaljenost od bale',
          materialNotTooTightlyPacked: 'Materijal nije previše zbijen',
          materialTooFlatAndTightlyPacked: 'Materijal je previše spljošten i zbijen',
          nearlyAllBaleFaceShown: 'Skoro cela prednja strana bale je prikazana',
          photoTooBlurry: 'Fotografija je previše mutna',
          someBackgroundIsOk: 'Malo pozadine je u redu',
          tooClose: 'Preblizu',
          tooManyBalesAngleNotStraightPoorQuality: 'Previše bala, ugao nije ravan, loš kvalitet',
          tooManyBalesManySidesVisible: 'Previše bala, vidi se više strana',
          tooManyBalesTooFarAway: 'Previše bala, predaleko',
          unclearWhichIsMainBale: 'Nije jasno koja je glavna bala',
        },
        intro: 'Evo primera dobrih i loših fotografija:',
      },
    },
    aiBriquette: {
      partOne: {
        imageAlt: 'Stanite najviše 0,5 metara dalje',
        instructions: '<bold>Stanite 0,5 metara</bold> ispred briketa.',
      },
      partTwo: {
        analysisInstruction: 'Kada analizirate paletu, <bold>fotografišite najmanje 6 briketa.</bold>',
        captions: {
          goodDistance: 'Dobra udaljenost',
          markingsVisible: 'Oznake su vidljive',
          noMarkingsVisible: 'Oznake nisu vidljive',
          okDistance: 'U redu udaljenost',
          tooDirty: 'Previše prljavo',
          tooShredded: 'Previše usitnjeno',
        },
        intro: 'Evo primera dobrih i loših fotografija:',
      },
    },
    aiLoose: {
      partTwo: {
        captions: {
          cannotSeeAllMaterial: 'Ne vidi se sav materijal',
          floorHasLargeMarkings: 'Pod ima velike oznake',
          materialInsideBlackBox: 'Materijal je unutar crnog okvira',
          materialNotOnGroundOrTable: 'Materijal nije na podu/stolu',
          materialShouldNotTouchPurpleLines: 'Materijal ne bi trebalo da dodiruje ljubičaste linije',
          materialSpreadVeryWell: 'Materijal je veoma dobro raspoređen',
          materialSpreadWell: 'Materijal je dobro raspoređen',
          materialTouchesEdge: 'Materijal dodiruje ivicu',
          shadow: 'Senka',
          squareFullOfMaterial: 'Kvadrat je pun materijala',
          tooHeapedAndTooBig: 'Previše nagomilano i preveliko',
        },
        intro: 'Evo primera dobrih i loših fotografija:',
      },
    },
    alert: {
      actions: {
        startGuide: 'Pokreni vodič - 1 min',
      },
      description: {
        obfuscatedAi: 'Pročitajte ovaj kratki vodič da napravite odgovarajuće fotografije.',
        withAi:
          'Pročitajte ovaj kratki vodič da napravite odgovarajuće fotografije za precizne AI rezultate. Loše fotografije = loši rezultati.',
      },
      title: 'Prvi put koristite {{modelTitle}}?',
    },
    autoCaptureTip: {
      message:
        'Savet! Radite brže i zadržite rukavice uz <autoCapture>Automatsko okidanje</autoCapture> koje automatski fotografiše umesto vas.',
    },
    common: {
      actions: {
        back: 'Nazad',
        finish: 'Završi',
        next: 'Dalje',
        skipGuide: 'Preskoči vodič',
      },
      models: {
        bale: 'balu',
        briquette: 'briket',
      },
    },
    complete: {
      message:
        'Kada završite, dodirnite <complete>Završi</complete> u kameri. Nakon toga ćete videti <qualityReport>Izveštaj o kvalitetu.</qualityReport>',
    },
    dialog: {
      stepLabel: 'Korak {{currentStep}}/{{maxSteps}}',
      title: 'Dobro došli u {{modelTitle}}!',
    },
    guideTitle: {
      obfuscatedAi: 'Naučite kako da pravilno fotografišete da biste dobili najbolje rezultate.',
      withAi: 'Naučite kako da pravilno fotografišete da biste dobili najbolje AI rezultate.',
    },
    offlineWarning: {
      message:
        'Savet! Uključite <offlineMode>Oflajn režim</offlineMode> da ne biste izgubili fotografije kada je veza slaba.',
    },
    reviewDeleteHelpText: {
      withAi:
        'Možete da vidite da li je {{model}} <accepted>prihvaćen</accepted> ili <rejected>odbijen</rejected> od strane AI. <delete>Obrišite</delete> i <retake>ponovo fotografišite</retake> neispravne slike.',
      withoutAi:
        'Možete da vidite da li je {{model}} <accepted>prihvaćen</accepted> ili <rejected>odbijen</rejected>. <delete>Obrišite</delete> i <retake>ponovo fotografišite</retake> neispravne slike.',
    },
    reviewDeleteTip: {
      message:
        'Savet! U izveštaju o kvalitetu takođe možete <reviewDelete>pregledati i obrisati</reviewDelete> i <addMorePhotos>dodati još fotografija</addMorePhotos>.',
    },
  },
  instructions: {
    photoInstructionsDrawer: {
      actions: {
        doNotShowAgain: 'Ne prikazuj ponovo',
        takePhoto: 'Fotografiši',
      },
      description:
        'Naš AI analizira otpremljene slike prema više parametara. Vaše otpremljene slike treba da izgledaju kao ispod.',
      frontFaceDescription:
        'Najčešći razlog za nevažeću sliku je orijentacija bale. Objekti u bali treba da budu spljošteni i okrenuti pravo.',
      frontFaceTitle: 'Šta je prednja strana?',
      imageAlt: {
        balePhotoExample: 'Primer fotografije bale',
        frontFaceBalePhoto: 'Fotografija prednje strane bale',
        treeRingBalePhoto: 'Fotografija bale sa godovima',
      },
      labels: {
        objectsFlattenedFaceOn: 'Objekti spljošteni i okrenuti pravo',
        objectsSquashedSideOn: 'Objekti zgnječeni i okrenuti sa strane',
      },
      title: 'Fotografisanje',
    },
  },
  materialComponents: {
    supportedComponent: {
      labels: {
        materialComponent: 'Komponenta materijala',
      },
    },
    supportedComponentValue: {
      adornments: {
        max: 'Maks: ',
        min: 'Min: ',
      },
      labels: {
        booleanValue: 'Logička vrednost',
        value: 'Vrednost',
      },
    },
  },
  nativeCachedPhotos: {
    notifications: {
      syncFailed: {
        description: 'Sačuvane su na telefonu. Pomerite se na mesto sa boljim signalom, pa ponovo sinhronizujte.',
        message: {
          few: '{{failedCount}} fotografije nisu otpremljene',
          many: '{{failedCount}} fotografija nije otpremljeno',
          one: '{{failedCount}} fotografija nije otpremljena',
          other: '{{failedCount}} fotografija nije otpremljeno',
        },
      },
    },
  },
  navigation: {
    sidebar: {
      closeAriaLabel: 'Zatvori',
    },
    userMenu: {
      belowSpecLoadEmailAlert: 'Teret ispod specifikacije',
      buyerEvidenceRequestCompletedEmailAlert: 'Završena pozivnica partneru',
      dailyProductionSummary: 'Dnevni pregled proizvodnje',
      dailyQualityDigest: 'Dnevni izveštaj o kvalitetu',
      emailNotificationsEnabled: 'Obaveštenja omogućena',
      language: 'Jezik',
      logOut: 'Odjavi se',
      manageCompany: 'Upravljaj kompanijom',
      notifications: 'E-pošta obaveštenja',
      weeklyQualityDigest: 'Nedeljni izveštaj o kvalitetu',
    },
  },
  networkStatus: {
    alert: {
      offlineMessage: 'Trenutno ste van mreže, dokazi će biti sačuvani lokalno dok se ponovo ne povežete.',
    },
    override: {
      label: 'Režim van mreže',
      notifications: {
        disabled: 'Režim van mreže je isključen',
        enabled: 'Režim van mreže je uključen',
      },
    },
  },
  offlinePhotosAlert: {
    all: 'sve',
    cacheDialog: {
      close: 'Zatvori',
      delete: 'Obriši',
      deleteError: 'Brisanje keširane fotografije nije uspelo',
      deleteMessage: 'Ova radnja će trajno obrisati ovu keširanu fotografiju i ne može da se opozove.',
      deleteSuccess: 'Keširana fotografija je obrisana',
      deleteTitle: 'Obriši keširanu fotografiju',
      description:
        'Pregledajte keširane fotografije, preuzmite one koje želite da sačuvate i obrišite fotografije koje ne treba sinhronizovati.',
      download: 'Preuzmi',
      downloadUnavailable: 'Fotografija nije dostupna za preuzimanje',
      empty: 'Nisu pronađene keširane fotografije.',
      nativeLoading: 'Učitavanje keširanih fotografija iz aplikacije...',
      orderRef: 'Referenca porudžbine: {{orderRef}}',
      parentDeleted: 'Izveštaj je obrisan',
      parentUnavailableDescription:
        'To može da se desi ako je neko iz vaše kompanije obrisao praznu sesiju sa drugog naloga. Obrišite ih ili ih preuzmite i ponovo otpremite u odgovarajući izveštaj.',
      parentUnavailableTitle: 'Ove keširane fotografije više nemaju izveštaj',
      parentVerificationFailed: 'Nije moguće proveriti dostupnost izveštaja. Pokušajte ponovo kasnije.',
      previewLoading: 'Učitavanje pregleda...',
      previewUnavailable: 'Pregled nije dostupan',
      reportId: 'Izveštaj: {{parentId}}',
      supplier: 'Dobavljač: {{supplier}}',
      takenAt: 'Snimljeno: {{takenAt}}',
      title: 'Keširane fotografije',
    },
    cancel: 'Otkaži',
    deleteAction: 'Obriši {{photoNoun}}',
    deleteMessage: 'Ova radnja će trajno obrisati fotografije i ne može da se opozove.',
    deleteTitle: 'Obriši fotografije',
    descriptionWithAi:
      'Sinhronizujte {{photoNoun}} da omogućite AI analizu i učinite {{objectPronoun}} vidljivim drugim korisnicima.',
    descriptionWithoutAi:
      'Sinhronizujte {{photoNoun}} da omogućite analizu i učinite {{objectPronoun}} vidljivim drugim korisnicima.',
    it: 'je',
    notifications: {
      cachedClearError: 'Brisanje keširanih fotografija nije uspelo',
      cachedClearSuccess: 'Keširane fotografije su obrisane',
      cachedSyncError: 'Sinhronizacija keširanih fotografija nije uspela',
      cachedSyncSuccess: 'Fotografije su otpremljene. Analiza može da potraje nekoliko minuta.',
      clearError: 'Brisanje oflajn fotografija nije uspelo',
      clearSuccess: 'Oflajn fotografije su obrisane',
      syncOfflineEvidenceError: 'Sinhronizacija oflajn dokaza nije uspela',
      syncPartialFailure: '{{successCount}} fotografija sinhronizovano, {{failedCount}} neuspešno',
      syncSuccess: '{{successCount}} fotografija sinhronizovano',
      uploadError: 'Otpremanje oflajn fotografija nije uspelo',
    },
    photo: 'fotografiju',
    photoIs: 'fotografija je',
    photos: 'fotografije',
    photosAre: 'fotografije su',
    syncAction: 'Sinhronizuj {{photoNoun}}',
    syncingMessage: 'Sinhronizacija fotografija je u toku. Ovo može da potraje...',
    them: 'ih',
    title: '{{count}} oflajn {{pluralVerb}} čeka sinhronizaciju',
    viewPhotos: 'Prikaži fotografije',
  },
  omnisearch: {
    noOptions: {
      withValue: 'Nema rezultata',
      withoutValue: 'Unesite tekst za pretragu',
    },
    placeholder: {
      desktop: 'Ctrl/Cmd + K za pretragu',
      mobile: 'Pretraga',
    },
  },
  siteMaterial: {
    tradeCompanySiteMaterialPicker: {
      actions: {
        addNewMaterial: 'Dodaj novi materijal',
        addNewMaterialAriaLabel: 'Dodaj novi materijal',
      },
      labels: {
        company: 'Kompanija',
        siteMaterial: 'Materijal lokacije',
      },
    },
  },
} satisfies I18nDictionaryShape<typeof featuresEnGb>;
